/**
 * Atlas → eZhire MySQL proxy hooks.
 *
 * Calls `supabase.functions.invoke('ezhire-mysql-proxy', { body: { queryId, params } })`
 * with TanStack Query. Modules under `modules/dispatch-center`,
 * `modules/ops-cockpit`, `modules/demand-intelligence`, `modules/finance-close`
 * use this hook for every chart and KPI.
 *
 * Per `Work Space/Build Briefs/ezhire-poc-demo/01-build-brief.md`:
 *   - No `refetchInterval` on module pages.
 *   - The Connectors page status pill polls every 60s — only allowed
 *     auto-refresh — handled by `useEzhireConnectorStatus`.
 *
 * Caching strategy (production-ready):
 *   - staleTime: 5 min — matches the cockpit's "live but not chatty" model
 *     and the user's mental model of "5-minute sync". A user navigating
 *     between pages within 5 minutes never re-fetches.
 *   - gcTime: 30 min — keep results in the LRU long after the last consumer
 *     unmounts. Returning to a page within 30 min hits cache instantly.
 *   - placeholderData: keepPreviousData — when params change (e.g. period
 *     selector), keep showing the prior result while the new one loads,
 *     so users never see a flash of empty.
 *
 * The QueryClient at `apps/web/src/app/providers.tsx` wires this whole
 * cache to localStorage persistence (PersistQueryClientProvider), so a
 * full page reload survives with prior data — pages render instantly.
 *
 * Also exports a URL-synced `usePeriod` helper that wraps the
 * `<TimePeriodSelector>` from `@ezhire/ui` with TanStack Router search
 * params for reload-survival.
 */

import {
  useQuery,
  useQueryClient,
  keepPreviousData,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  resolvePeriod,
  type TimePeriodKey,
  type TimePeriodValue,
} from '@ezhire/ui';
import { useSupabaseClient } from './use-supabase';

export interface EzhireQueryResult<Row> {
  ok: true;
  data: Row[];
  cachedAt: string;
  ttl: number;
  executionMs: number;
  cacheHit: boolean;
  truncated: boolean;
  rowCount: number;
  queryId: string;
}

interface EzhireQueryError {
  error: string;
  detail?: string | string[];
}

export interface UseEzhireQueryOptions {
  /** When false, the query won't fire — used for connector-red states. */
  enabled?: boolean;
}

export function useEzhireQuery<Row = Record<string, unknown>>(
  queryId: string,
  params: Record<string, unknown> = {},
  options: UseEzhireQueryOptions = {},
): UseQueryResult<EzhireQueryResult<Row>, Error> {
  const supabase = useSupabaseClient();
  return useQuery({
    queryKey: ['ezhire', queryId, params],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('ezhire-mysql-proxy', {
        body: { queryId, params },
      });
      if (error) throw new Error(error.message);
      if ((data as EzhireQueryError)?.error) {
        const e = data as EzhireQueryError;
        const detail = Array.isArray(e.detail) ? e.detail.join('; ') : e.detail;
        throw new Error(detail ? `${e.error}: ${detail}` : e.error);
      }
      return data as EzhireQueryResult<Row>;
    },
    enabled: options.enabled ?? true,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 1,
  });
}

export interface ConnectorTestResult {
  ok: boolean;
  message: string;
  executionMs: number;
  serverInfo?: { user?: string; host?: string; version?: string } | null;
  checkedAt: string;
}

/**
 * Background prefetch for sibling-page eZhire queries.
 *
 * Mounted on a module landing page to warm the cache for the screens
 * the user is most likely to navigate to next. Each prefetch fires
 * inside `requestIdleCallback` (with a `setTimeout` fallback) so it
 * never competes with the foreground render.
 *
 * The prefetched results land in the same `['ezhire', queryId, params]`
 * key the consumer hook reads from, so when the user navigates the
 * page renders instantly from cache (no spinner, no flash).
 *
 * Idempotent: re-running with the same descriptors is cheap because
 * fresh entries (within `staleTime`) short-circuit inside TanStack.
 */
export interface EzhirePrefetchDescriptor {
  queryId: string;
  params?: Record<string, unknown>;
}

export function useEzhirePrefetch(
  descriptors: EzhirePrefetchDescriptor[],
  options: { enabled?: boolean } = {},
): void {
  const supabase = useSupabaseClient();
  const queryClient = useQueryClient();
  const enabled = options.enabled ?? true;
  // Stringify-stable signature so we don't re-run prefetch on every
  // parent re-render — only when the descriptor list actually changes.
  const signature = useMemo(() => JSON.stringify(descriptors), [descriptors]);
  const fired = useRef<string>('');

  useEffect(() => {
    if (!enabled) return;
    if (fired.current === signature) return;
    fired.current = signature;

    const idle = (cb: () => void) => {
      const w = typeof window !== 'undefined'
        ? (window as unknown as { requestIdleCallback?: (cb: () => void) => number })
        : null;
      if (w?.requestIdleCallback) {
        w.requestIdleCallback(cb);
      } else {
        setTimeout(cb, 200);
      }
    };

    for (const desc of descriptors) {
      idle(() => {
        void queryClient.prefetchQuery({
          queryKey: ['ezhire', desc.queryId, desc.params ?? {}],
          queryFn: async () => {
            const { data, error } = await supabase.functions.invoke('ezhire-mysql-proxy', {
              body: { queryId: desc.queryId, params: desc.params ?? {} },
            });
            if (error) throw new Error(error.message);
            if ((data as EzhireQueryError)?.error) {
              const e = data as EzhireQueryError;
              const detail = Array.isArray(e.detail) ? e.detail.join('; ') : e.detail;
              throw new Error(detail ? `${e.error}: ${detail}` : e.error);
            }
            return data;
          },
          staleTime: 5 * 60_000,
        });
      });
    }
  }, [signature, descriptors, enabled, queryClient, supabase]);
}

/** Connectors-page-only auto-refresh hook (60s). Per brief §9. */
export function useEzhireConnectorStatus(options: { autoRefresh?: boolean } = {}) {
  const supabase = useSupabaseClient();
  return useQuery({
    queryKey: ['ezhire-connector-status'],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('ezhire-test-connection', {
        body: {},
      });
      if (error) throw new Error(error.message);
      return data as ConnectorTestResult;
    },
    refetchInterval: options.autoRefresh === false ? false : 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
    retry: 1,
  });
}

// ----- Period state hook ----------------------------------------------------

const VALID_KEYS: TimePeriodKey[] = ['today', 'yesterday', '3d', '7d', '15d', '30d', 'custom'];

export function usePeriod(defaultPeriod: TimePeriodKey = '7d'): {
  period: TimePeriodValue;
  setPeriod: (key: TimePeriodKey) => void;
  refresh: () => void;
} {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { period?: string };

  const key = useMemo<TimePeriodKey>(() => {
    const raw = search.period;
    if (raw && VALID_KEYS.includes(raw as TimePeriodKey)) return raw as TimePeriodKey;
    return defaultPeriod;
  }, [search.period, defaultPeriod]);

  const period = useMemo(() => resolvePeriod(key), [key]);

  const setPeriod = useCallback(
    (next: TimePeriodKey) => {
      void navigate({
        // The reducer signature varies across TanStack Router versions; cast
        // keeps this hook portable without binding to one router type.
        search: ((prev: Record<string, unknown>) => ({ ...prev, period: next })) as never,
        replace: false,
      } as never);
    },
    [navigate],
  );

  const refresh = useCallback(() => {
    void navigate({
      search: ((prev: Record<string, unknown>) => ({
        ...prev,
        period: key,
        _r: Date.now(),
      })) as never,
      replace: true,
    } as never);
  }, [navigate, key]);

  return { period, setPeriod, refresh };
}
